import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { countActiveOutletsForRoute } from './routeOutlets';
import { OutletVisit } from '../types';

// Create a new outlet visit record
export const createOutletVisit = async (
  outletId: string,
  outletName: string,
  salesmanId: string,
  salesmanName: string,
  routeId: string,
  date: string
): Promise<{ success: boolean; visitId?: string; message: string }> => {
  try {
    // Check if visit already exists for this outlet today
    const existingVisitQuery = query(
      collection(db, 'outletVisits'),
      where('outletId', '==', outletId),
      where('salesmanId', '==', salesmanId),
      where('date', '==', date)
    );
    
    const existingVisits = await getDocs(existingVisitQuery);
    
    if (!existingVisits.empty) {
      // Return existing visit ID instead of creating duplicate
      return {
        success: true,
        visitId: existingVisits.docs[0].id,
        message: 'Visit already exists for this outlet today'
      };
    }

    const visitData: Omit<OutletVisit, 'id'> = {
      date,
      routeId,
      outletId,
      outletName,
      salesmanId,
      salesmanName,
      visitStartTime: serverTimestamp(),
      status: 'visit_only',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'outletVisits'), visitData);
    
    return {
      success: true,
      visitId: docRef.id,
      message: 'Outlet visit created successfully'
    };

  } catch (error) {
    console.error('Error creating outlet visit:', error);
    return {
      success: false,
      message: `Error creating outlet visit: ${error}`
    };
  }
};

// Update outlet visit with transaction details
export const updateOutletVisitWithTransaction = async (
  outletId: string,
  salesmanId: string,
  date: string,
  transactionData: {
    saleAmount: number;
    replaceQty: number;
    totalSaleQty: number;
    status?: OutletVisit['status'];
    noSaleReason?: OutletVisit['noSaleReason'];
    shelfPhotoUrl?: string;
  }
): Promise<{ success: boolean; message: string }> => {
  try {
    // Find the visit record for this outlet today
    const visitQuery = query(
      collection(db, 'outletVisits'),
      where('outletId', '==', outletId),
      where('salesmanId', '==', salesmanId),
      where('date', '==', date)
    );
    
    const visitSnapshot = await getDocs(visitQuery);
    
    if (visitSnapshot.empty) {
      return {
        success: false,
        message: 'No visit record found for this outlet'
      };
    }

    const visitDoc = visitSnapshot.docs[0];
    
    // Determine visit status - use provided status or calculate based on transaction data
    let status: OutletVisit['status'] = transactionData.status || 'visit_only';
    
    if (!transactionData.status) {
      // Auto-determine status based on transaction data if not provided
      if (transactionData.totalSaleQty > 0 && transactionData.replaceQty > 0) {
        status = 'sale_and_replace';
      } else if (transactionData.totalSaleQty > 0) {
        status = 'sale';
      } else if (transactionData.replaceQty > 0) {
        status = 'replace_only';
      }
    }

    // Prepare update data
    const updateData: any = {
      txnTime: serverTimestamp(),
      status,
      saleAmount: transactionData.saleAmount,
      replaceQty: transactionData.replaceQty,
      updatedAt: serverTimestamp()
    };

    // Add no-sale specific fields if provided
    if (transactionData.noSaleReason) {
      updateData.noSaleReason = transactionData.noSaleReason;
    }
    
    if (transactionData.shelfPhotoUrl) {
      updateData.shelfPhotoUrl = transactionData.shelfPhotoUrl;
    }

    // Update the visit record
    await updateDoc(visitDoc.ref, updateData);

    return {
      success: true,
      message: 'Outlet visit updated with transaction details'
    };

  } catch (error) {
    console.error('Error updating outlet visit:', error);
    return {
      success: false,
      message: `Error updating outlet visit: ${error}`
    };
  }
};

// Get all outlet visits for a route on a specific date
export const getOutletVisitsForRoute = async (
  routeId: string,
  date: string,
  salesmanId?: string
): Promise<OutletVisit[]> => {
  try {
    let q = query(
      collection(db, 'outletVisits'),
      where('routeId', '==', routeId),
      where('date', '==', date)
    );

    if (salesmanId) {
      q = query(q, where('salesmanId', '==', salesmanId));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as OutletVisit));

  } catch (error) {
    console.error('Error getting outlet visits for route:', error);
    return [];
  }
};

export type RouteVisitStats = {
  totalOutletsInRoute: number;
  visitedUniqueOutlets: number;
  notVisited: number;
  visitedByStatus: Record<'sale' | 'sale_and_replace' | 'replace_only' | 'visit_only' | 'no_sale', number>;
};

// Compute visit statistics for a route and date (optionally scoped to salesman)
export const getVisitStatsForRouteDate = async (
  routeId: string,
  date: string,
  salesmanId?: string
): Promise<RouteVisitStats> => {
  try {
    // Query visits for the route and date
    let q = query(
      collection(db, 'outletVisits'),
      where('routeId', '==', routeId),
      where('date', '==', date)
    );
    if (salesmanId) {
      q = query(q, where('salesmanId', '==', salesmanId));
    }

    const snapshot = await getDocs(q);
    const visits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as OutletVisit[];

    // Aggregate
    const visitedByStatus: RouteVisitStats['visitedByStatus'] = {
      sale: 0,
      sale_and_replace: 0,
      replace_only: 0,
      visit_only: 0,
      no_sale: 0
    };
    const uniqueOutletIds = new Set<string>();
    for (const v of visits) {
      uniqueOutletIds.add(v.outletId);
      if (visitedByStatus[v.status as keyof RouteVisitStats['visitedByStatus']] !== undefined) {
        visitedByStatus[v.status as keyof RouteVisitStats['visitedByStatus']]++;
      }
    }

    // Total active outlets in the route
    const totalOutletsInRoute = await countActiveOutletsForRoute(routeId);
    const visitedUniqueOutlets = uniqueOutletIds.size;
    const notVisited = Math.max(0, totalOutletsInRoute - visitedUniqueOutlets);

    return { totalOutletsInRoute, visitedUniqueOutlets, notVisited, visitedByStatus };
  } catch (error) {
    console.error('Error computing visit stats:', error);
    return {
      totalOutletsInRoute: 0,
      visitedUniqueOutlets: 0,
      notVisited: 0,
      visitedByStatus: { sale: 0, sale_and_replace: 0, replace_only: 0, visit_only: 0, no_sale: 0 }
    };
  }
};

// Get visit status for a specific outlet on a specific date
export const getOutletVisitStatus = async (
  outletId: string,
  date: string,
  salesmanId: string
): Promise<{
  visited: boolean;
  status?: OutletVisit['status'];
  visitTime?: any;
  saleAmount?: number;
}> => {
  try {
    const visitQuery = query(
      collection(db, 'outletVisits'),
      where('outletId', '==', outletId),
      where('salesmanId', '==', salesmanId),
      where('date', '==', date)
    );
    
    const snapshot = await getDocs(visitQuery);
    
    if (snapshot.empty) {
      return { visited: false };
    }

    const visitData = snapshot.docs[0].data() as OutletVisit;
    return {
      visited: true,
      status: visitData.status,
      visitTime: visitData.visitStartTime,
      saleAmount: visitData.saleAmount || 0
    };

  } catch (error) {
    console.error('Error getting outlet visit status:', error);
    return { visited: false };
  }
};

// Get visit statistics for a salesman on a specific date
export const getVisitStatistics = async (
  salesmanId: string,
  date: string,
  routeId?: string
): Promise<{
  totalVisits: number;
  visitedOutlets: number;
  salesVisits: number;
  totalSalesAmount: number;
}> => {
  try {
    let q = query(
      collection(db, 'outletVisits'),
      where('salesmanId', '==', salesmanId),
      where('date', '==', date)
    );

    if (routeId) {
      q = query(q, where('routeId', '==', routeId));
    }

    const snapshot = await getDocs(q);
    const visits = snapshot.docs.map(doc => doc.data() as OutletVisit);

    const totalVisits = visits.length;
    const visitedOutlets = visits.length; // Each visit is a unique outlet
    const salesVisits = visits.filter(visit => 
      visit.status === 'sale' || visit.status === 'sale_and_replace'
    ).length;
    const totalSalesAmount = visits.reduce((sum, visit) => 
      sum + (visit.saleAmount || 0), 0
    );

    return {
      totalVisits,
      visitedOutlets,
      salesVisits,
      totalSalesAmount
    };

  } catch (error) {
    console.error('Error getting visit statistics:', error);
    return {
      totalVisits: 0,
      visitedOutlets: 0,
      salesVisits: 0,
      totalSalesAmount: 0
    };
  }
};
